// organization-properties-helper.js
// Reads per-org UI properties (e.g. sidebar link visibility) from Firestore.
// Uses the same Firebase app instance that is already initialized by FirebaseAuthHelper.

import { getApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const COLLECTION = "organization-properties";

/**
 * Fetches the organization properties from Firestore.
 * Returns an object like { sidebarLinks: { link1: true, link2: false, ... } }
 * or null if no document exists for the given orgId (meaning: show all links).
 *
 * @param {string} orgId - The organization ID (document key in Firestore)
 * @returns {Promise<{ sidebarLinks: Record<string, boolean> } | null>}
 */
export const fetchOrgProperties = async (orgId) => {
  if (!orgId) return null;

  try {
    const app = getApp();
    const db = getFirestore(app);
    const docRef = doc(db, COLLECTION, orgId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data() || null;
  } catch (error) {
    console.error("Error fetching org properties from Firestore:", error);
    return null;
  }
};

/**
 * Returns true if a given sidebar link key should be shown for this org.
 * Defaults to true (visible) when no properties are configured.
 *
 * @param {Record<string, boolean> | null | undefined} sidebarLinks
 * @param {string} linkKey - e.g. "link1", "link2", …, "link11"
 * @returns {boolean}
 */
export const isSidebarLinkEnabled = (sidebarLinks, linkKey) => {
  if (!sidebarLinks) return true;
  const value = sidebarLinks[linkKey];
  if (typeof value !== "boolean") return true;
  return value;
};
